import { Component, DestroyRef, OnDestroy, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { PropertyGroupDescription } from '@grapecity/wijmo'
import { CellEditEndingEventArgs, Column, DataMap, FlexGrid } from '@grapecity/wijmo.grid'
import { NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase } from '@ng-bootstrap/ng-bootstrap'

import {
    BaseComponent,
    FabShopStockProduct,
    FabShopStockProductType,
    FabShopStockProductTypeMaterial,
    Grid,
    ListItem,
    ModalSize,
} from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { FabShopStockProductTypeComponent } from './fab-shop-stock-product-type.component'

const UNASSIGNED_LABEL = '(Unassigned)'

@Component({
    templateUrl: './fab-shop-stock-products.component.html',
    standalone: true,
    imports: [
        FormsModule,
        NgbNav,
        NgbNavItem,
        NgbNavItemRole,
        NgbNavLink,
        NgbNavLinkBase,
        WjGridModule,
        WjGridFilterModule,
        AutoSizeDirective,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        GridExportComponent,
    ],
})
export class FabShopStockProductsComponent extends BaseComponent implements OnDestroy {
    private destroyRef = inject(DestroyRef)

    active = 1

    productsGrid: Grid<FabShopStockProduct>
    typesGrid: Grid<FabShopStockProductType>

    typeMap = new DataMap([], 'value', 'display')
    typeEditMap = new DataMap([], 'value', 'display')

    private materialToAssignment = new Map<string, FabShopStockProductTypeMaterial>()
    private typesRefreshHandler = () => this.refresh()

    constructor() {
        super()

        this.productsGrid = new Grid<FabShopStockProduct>(this.services)
            .isReadOnly(false)
            .editOnDoubleClick(false)
            .filterBy(item => [item.material, item.description, item.typeDisplayName])

        this.typesGrid = new Grid<FabShopStockProductType>(this.services)
            .setupModalEditing(
                this.services.data.fabShopStockProductTypes,
                'Fab Shop Stock Product Type',
                FabShopStockProductTypeComponent,
                () => new FabShopStockProductType(),
                this,
                ModalSize.small,
            )
            .filterBy(item => [item.name])
        this.typesGrid.modified.on(this.typesRefreshHandler)

        this.refresh()
    }

    ngOnDestroy() {
        this.typesGrid.modified.off(this.typesRefreshHandler)
    }

    refresh() {
        const sub = this.services.data
            .load(
                true,
                true,
                this.services.data.get<FabShopStockProduct[]>('/api/VpData/QueryFabShopStockProducts'),
                this.services.data.fabShopStockProductTypes.queryAll(),
                this.services.data.fabShopStockProductTypeMaterials.queryAll(),
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: result => {
                    const products = result[0] as FabShopStockProduct[]
                    const types = result[1] as FabShopStockProductType[]
                    const assignments = result[2] as FabShopStockProductTypeMaterial[]

                    this.buildTypeMaps(types)
                    this.applyAssignments(products, assignments, types)
                    this.typesGrid.items = types
                    this.initialLoadingComplete()
                },
            })
        this.cancelableRequest('', true, sub)
    }

    private buildTypeMaps(types: FabShopStockProductType[]) {
        this.typeMap = new DataMap(
            types.map(type => new ListItem<string>(type.id, type.name)),
            'value',
            'display',
        )
        this.typeEditMap = new DataMap(
            [
                new ListItem<string>('', UNASSIGNED_LABEL),
                ...types.filter(type => !type.archived).map(type => new ListItem<string>(type.id, type.name)),
            ],
            'value',
            'display',
        )
    }

    private applyAssignments(products: FabShopStockProduct[], assignments: FabShopStockProductTypeMaterial[], types: FabShopStockProductType[]) {
        const typeById = new Map(types.map(type => [type.id, type]))
        this.materialToAssignment.clear()

        assignments.forEach(assignment => {
            const key = this.materialKey(assignment.matlGroup, assignment.material)
            if (!this.materialToAssignment.has(key)) {
                this.materialToAssignment.set(key, assignment)
            }
        })

        products.forEach(product => {
            const assignment = this.materialToAssignment.get(this.materialKey(product.matlGroup, product.material))
            const type = assignment ? typeById.get(assignment.fabShopStockProductTypeId) : undefined

            product.typeId = type?.id ?? null
            product.typeName = type?.name ?? ''
            product.typeSortOrder = type?.sortOrder ?? Number.MAX_SAFE_INTEGER
            product.typeDisplayName = product.typeName || UNASSIGNED_LABEL
            product['fabShopStockProductTypeId'] = product.typeId ?? ''
        })

        this.productsGrid.items = products
        this.applyProductGrouping()
    }

    private applyProductGrouping() {
        const view = this.productsGrid.view
        view.groupDescriptions.clear()
        view.groupDescriptions.push(new PropertyGroupDescription('typeDisplayName'))
        view.refresh()
        if (this.productsGrid.isInitialized && this.productsGrid.flexGrid) {
            // Level 1 expands all groups so autoSizeColumns measures full cell content, not just headers.
            this.productsGrid.flexGrid.collapseGroupsToLevel(1)
            setTimeout(() => this.productsGrid.flexGrid?.autoSizeColumns())
        }
    }

    onProductsGridInitialized(grid: Parameters<Grid<FabShopStockProduct>['initialize']>[0]) {
        this.productsGrid.initialize(grid)
        this.applyProductGrouping()
    }

    cellBeginningEdit(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        const col = flexGrid.columns[args.col] as Column
        if (col.binding === 'fabShopStockProductTypeId') {
            col.dataMap = this.typeEditMap
        }
    }

    cellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        const col = flexGrid.columns[args.col] as Column
        if (col.binding === 'fabShopStockProductTypeId') {
            col.dataMap = this.typeMap
        }

        if (args.cancel || col.binding !== 'fabShopStockProductTypeId') return

        const product = flexGrid.rows[args.row].dataItem as FabShopStockProduct
        const newTypeId = (product['fabShopStockProductTypeId'] as string) || ''
        const key = this.materialKey(product.matlGroup, product.material)
        const existing = this.materialToAssignment.get(key)

        if (newTypeId) {
            if (existing) {
                if (existing.fabShopStockProductTypeId !== newTypeId) {
                    existing.fabShopStockProductTypeId = newTypeId
                    this.services.data.fabShopStockProductTypeMaterials.edit(existing).subscribe({
                        next: () => this.refresh(),
                        error: () => this.onAssignmentSaveError(),
                    })
                }
            } else {
                const assignment = new FabShopStockProductTypeMaterial()
                assignment.matlGroup = product.matlGroup
                assignment.material = product.material
                assignment.fabShopStockProductTypeId = newTypeId
                this.services.data.fabShopStockProductTypeMaterials.add(assignment).subscribe({
                    next: saved => {
                        this.materialToAssignment.set(key, saved)
                        this.refresh()
                    },
                    error: () => this.onAssignmentSaveError(),
                })
            }
        } else if (existing) {
            this.services.data.fabShopStockProductTypeMaterials.delete(existing).subscribe({
                next: () => {
                    this.materialToAssignment.delete(key)
                    this.refresh()
                },
                error: () => this.onAssignmentSaveError(),
            })
        }
    }

    private materialKey(matlGroup: number, material: string) {
        return `${matlGroup}|${material}`
    }

    private onAssignmentSaveError() {
        this.services.notification.error('The product type assignment was not successfully saved')
    }

    get canEdit() {
        return true
    }
}
